import { useEffect, useState } from "react";
import { TemplateEditor } from "../components/TemplateEditor.js";
import { api, ApiError } from "../lib/api.js";
import type { PromptTemplate } from "../lib/types.js";

export function Templates() {
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await api<{ items: PromptTemplate[] }>("/api/templates");
        if (cancelled) return;
        setTemplates(res.items);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) return;
        setError(err instanceof Error ? err.message : "Could not load templates.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const selected = templates.find((t) => t.templateId === selectedId) ?? null;

  return (
    <div className="mx-auto max-w-4xl p-6">
      <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Templates</h1>
      {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
      {loading && <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading...</p>}

      {!loading && (
        <div className="mt-4 grid gap-6 sm:grid-cols-[220px_1fr]">
          <ul className="divide-y divide-gray-200 rounded-md border border-gray-200 bg-white dark:divide-gray-800 dark:border-gray-800 dark:bg-gray-900">
            {templates.map((t) => (
              <li key={t.templateId}>
                <button
                  type="button"
                  onClick={() => setSelectedId(t.templateId)}
                  className={`block w-full px-3 py-2 text-left text-sm ${
                    t.templateId === selectedId
                      ? "bg-(--accent) text-(--accent-contrast)"
                      : "text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
                  }`}
                >
                  <div className="font-medium">{t.name}</div>
                  <div className="text-xs opacity-70">{t.contentType}</div>
                </button>
              </li>
            ))}
          </ul>

          <div>
            {selected ? (
              <TemplateEditor
                key={selected.templateId}
                template={selected}
                onSaved={(updated) =>
                  setTemplates((prev) => prev.map((t) => (t.templateId === updated.templateId ? updated : t)))
                }
              />
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">Select a template to edit it.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
